<template>
    <MainContent title="Editar equipo">
        <template v-if="loading">
            <Skeleton padding="tw:p-6" variant="tw:bg-white tw:shadow-sm tw:rounded-xl tw:border tw:border-gray-200">
                <template #skeleton>
                    <div class="tw:flex tw:flex-col tw:gap-6">
                        <div class="tw:space-y-2">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-16 tw:rounded" />
                            <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:rounded-lg" />
                        </div>
                        <div class="tw:space-y-2">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-24 tw:rounded" />
                            <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:rounded-lg" />
                        </div>
                        <div class="tw:space-y-2">
                            <SkeletonItem height="tw:h-3" customClass="tw:w-32 tw:rounded" />
                            <SkeletonItem height="tw:h-3" customClass="tw:w-64 tw:rounded" />
                            <SkeletonItem height="tw:h-54" customClass="tw:w-full tw:rounded-lg" />
                        </div>
                        <div class="tw:flex tw:justify-end">
                            <SkeletonItem height="tw:h-10" customClass="tw:w-24 tw:rounded-lg" />
                        </div>
                    </div>
                </template>
            </Skeleton>
        </template>
        <template v-else>
            <Card>
                <FormEquipo
                    :equipo="equipo"
                    :lista-id="listaId"
                    :saving="saving"
                    :representantes="representantes"
                    :representantes-loading="loadingRepresentantes"
                    @terminado="editarEquipo"
                />
            </Card>
        </template>
    </MainContent>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue';
    import { useRoute, useRouter } from '@/composables/common/router/useRouter';
    import { useToast } from '@/composables/common/useToast';
    import store from '@/store';
    import { config } from '@/config.js';
    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import FormEquipo from '@/modules/admin/bases/equipos/components/FormEquipo.vue';
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton';

    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const saving = ref(false);
    const loading = ref(true);
    const loadingRepresentantes = ref(true);
    const equipo = ref(null);
    const representantes = ref([]);

    const listaId = computed(() => route.params.lista_id);
    const equipoId = computed(() => route.params.equipo_id);

    async function getEquipo() {
        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.equipos.show'),
                {
                    lista_id: listaId.value,
                    equipo_id: equipoId.value,
                    filters: {}
                }
            );
            equipo.value = res.data.data;
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al cargar el equipo');
        }
    }

    async function getRepresentantes() {
        try {
            const params = { lista_id: listaId.value, filters: {} };
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.get.representantes'),
                params
            );
            representantes.value = res.data.representantes || [];
        } catch (error) {
            toast.error('Error al cargar los representantes');
        } finally {
            loadingRepresentantes.value = false;
        }
    }

    async function editarEquipo(formData) {
        saving.value = true;

        const payload = {
            ...formData,
            filters: {},
            lista_id: listaId.value,
            equipo_id: equipoId.value
        };

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.equipos.update'),
                payload
            );
            toast.success(res.data.message);
            router.push({ name: 'back.bases.equipos', params: { lista_id: listaId.value } });
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al editar el equipo');
        } finally {
            saving.value = false;
        }
    }

    onMounted(async() => {
        await Promise.all([
            getEquipo(),
            getRepresentantes()
        ]);
        loading.value = false;
    });
</script>
